import type { ReactNode } from "react";
import { formatTickTimestamp, formatValue, type FormatOptions, isLikelyTimestamp } from "./format";
import styles from "./ChartContainer.module.css";

export type RechartsTooltipPayloadEntry = {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string | null;
  color?: string;
  payload?: Record<string, unknown>;
};

export interface RechartsTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<RechartsTooltipPayloadEntry>;
  label?: string | number;
}

export interface ChartTooltipProps extends RechartsTooltipProps {
  /** Format hint for the value column. Defaults to `auto`. */
  valueFormat?: FormatOptions["format"];
  /** Optional override for the label rendering — useful when the label
   *  is a category code that should be looked up in a friendly name map. */
  formatLabel?: (label: string | number | undefined) => string;
}

/**
 * Standard recharts tooltip used by every chart widget.
 *
 * - Formats numeric values via the shared `formatValue` so a dashboard
 *   with mixed widgets ("123.4K", "12.3%", "1 h 5 m") all read in the
 *   same style.
 * - Renders timestamps with `formatTickTimestamp` so the tooltip header
 *   matches the x-axis.
 * - Truncates long series names so wide categorical labels don't push
 *   the value column off the right edge of the tooltip.
 * - Tolerates partial payloads (Recharts omits `value` when a series
 *   datum is null, etc).
 */
export function ChartTooltip({
  active,
  payload,
  label,
  valueFormat = "auto",
  formatLabel,
}: ChartTooltipProps): ReactNode {
  if (!active || !payload || payload.length === 0) return null;

  const renderedLabel = formatLabel
    ? formatLabel(label)
    : isLikelyTimestamp(label)
      ? formatTickTimestamp(label)
      : label === undefined || label === null
        ? ""
        : String(label);

  return (
    <div className={styles.tooltip}>
      {renderedLabel ? (
        <div className={styles.tooltipLabel}>{renderedLabel}</div>
      ) : null}
      {payload.map((entry, index) => {
        const name = String(entry.name ?? entry.dataKey ?? index);
        return (
          <div key={`${name}-${index}`} className={styles.tooltipRow}>
            {entry.color ? (
              <span
                className={styles.tooltipSwatch}
                style={{ background: entry.color }}
              />
            ) : null}
            <span className={styles.tooltipName}>{name}</span>
            <span className={styles.tooltipValue}>
              {entry.value === null || entry.value === undefined
                ? "—"
                : formatValue(entry.value, { format: valueFormat })}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export interface DeltaTooltipRow {
  label: string;
  value: unknown;
  delta?: { fraction: number | null; absolute: number; direction: -1 | 0 | 1 };
}

export interface ChartTooltipDeltaProps {
  title?: string;
  rows: DeltaTooltipRow[];
  valueFormat?: FormatOptions["format"];
}

/**
 * Tooltip variant that surfaces a delta row (used by stat-with-delta
 * and sparkline-stat). Renders ↑ / ↓ / · with semantic color from the
 * shared palette.
 */
export function ChartTooltipDelta({ title, rows, valueFormat = "auto" }: ChartTooltipDeltaProps): ReactNode {
  return (
    <div className={styles.tooltip}>
      {title ? <div className={styles.tooltipLabel}>{title}</div> : null}
      {rows.map((row, index) => (
        <div key={`${row.label}-${index}`} className={styles.tooltipRow}>
          <span className={styles.tooltipName}>{row.label}</span>
          <span className={styles.tooltipValue}>
            {formatValue(row.value, { format: valueFormat })}
          </span>
          {row.delta ? (
            <span
              className={
                row.delta.direction > 0
                  ? styles.tooltipDeltaUp
                  : row.delta.direction < 0
                    ? styles.tooltipDeltaDown
                    : styles.tooltipDeltaFlat
              }
            >
              {row.delta.direction > 0 ? "↑" : row.delta.direction < 0 ? "↓" : "·"}
              {row.delta.fraction !== null
                ? ` ${(row.delta.fraction * 100).toFixed(1)}%`
                : ` ${formatValue(row.delta.absolute, { format: valueFormat })}`}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}
